import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { format, parseISO } from 'date-fns'
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { api } from '../../services/api'
import { useAccount } from '../../hooks/useAccount'

const numberFormatter = new Intl.NumberFormat('de-DE')

const formatRate = (value) => `${Math.round((value || 0) * 100)}%`

const formatTooltipLabel = (value) => {
  try {
    return format(parseISO(value), 'PP')
  } catch (err) {
    return value
  }
}

function AnalyticsPage() {
  const { t } = useTranslation('analytics')
  const { selectedAccountId, isLoadingAccounts } = useAccount()

  const RANGE_OPTIONS = useMemo(() => [
    { label: t('rangeOptions.sevenDays'), value: '7d', days: 7 },
    { label: t('rangeOptions.thirtyDays'), value: '30d', days: 30 },
    { label: t('rangeOptions.ninetyDays'), value: '90d', days: 90 },
  ], [t])

  const [range, setRange] = useState(RANGE_OPTIONS[0])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [stories, setStories] = useState([])
  const [performance, setPerformance] = useState(null)

  const loadAnalytics = useCallback(async (selectedRange) => {
    try {
      setLoading(true)
      setError(null)

      const sinceDate = new Date()
      sinceDate.setDate(sinceDate.getDate() - selectedRange.days)

      const query = { since: sinceDate.toISOString(), include_history: true, history_limit: 20 }
      if (selectedAccountId) {
        query.instagram_account_id = selectedAccountId
      }

      const [storyAnalytics, overallPerformance] = await Promise.all([
        api.getStoryAnalytics(query),
        api.getPerformanceAnalytics(query),
      ])

      setStories(storyAnalytics?.stories ?? [])
      setPerformance(overallPerformance?.summary ?? null)
    } catch (err) {
      console.error('Failed to load analytics', err)
      setError(err.message || t('error.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [selectedAccountId, t])

  useEffect(() => {
    if (!isLoadingAccounts) {
      loadAnalytics(range)
    }
  }, [selectedAccountId, isLoadingAccounts, loadAnalytics, range])

  const chartData = useMemo(() => {
    if (!performance?.time_series) {
      return []
    }

    return performance.time_series.map((point) => ({
      date: point.date,
      views: point.views,
      reach: point.reach,
      impressions: point.impressions,
      completionRate: Math.round((point.completion_rate || 0) * 100),
      engagementRate: Math.round((point.engagement_rate || 0) * 100),
    }))
  }, [performance])

  const topStories = useMemo(() => {
    if (!stories || stories.length === 0) {
      return []
    }

    return stories
      .slice()
      .sort((a, b) => (b.totals?.views || 0) - (a.totals?.views || 0))
      .slice(0, 8)
  }, [stories])

  return (
    <div className="space-y-8 px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('pageTitle')}</h1>
          <p className="mt-2 text-gray-600">{t('pageSubtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {RANGE_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => setRange(option)}
              className={`px-3 py-2 text-sm font-medium rounded-md border ${
                range.value === option.value
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <p className="font-medium">{t('error.loadFailed')}</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      ) : (
        <>
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card">
              <p className="text-sm text-gray-600">{t('stats.totalViews')}</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {numberFormatter.format(performance?.totals?.views || 0)}
              </p>
            </div>
            <div className="card">
              <p className="text-sm text-gray-600">{t('stats.totalReach')}</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {numberFormatter.format(performance?.totals?.reach || 0)}
              </p>
            </div>
            <div className="card">
              <p className="text-sm text-gray-600">{t('stats.engagementRate')}</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {formatRate(performance?.average_rates?.engagement_rate)}
              </p>
            </div>
            <div className="card">
              <p className="text-sm text-gray-600">{t('stats.completionRate')}</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {formatRate(performance?.average_rates?.completion_rate)}
              </p>
            </div>
          </section>

          <section className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('trends.title')}</h2>
            {chartData.length === 0 ? (
              <p className="text-sm text-gray-600">{t('trends.noData')}</p>
            ) : (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={(value) => format(parseISO(value), 'dd.MM.')} />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => `${value}%`} />
                    <Tooltip labelFormatter={formatTooltipLabel} />
                    <Legend />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="views"
                      name={t('chart.views')}
                      stroke="#2563eb"
                      fill="#2563eb33"
                    />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="reach"
                      name={t('chart.reach')}
                      stroke="#10b981"
                      fill="#10b98133"
                    />
                    <Area
                      yAxisId="right"
                      type="monotone"
                      dataKey="completionRate"
                      name={t('chart.completionPercent')}
                      stroke="#f59e0b"
                      fill="#f59e0b22"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </section>

          <section className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('topStories.title')}</h2>
            {topStories.length === 0 ? (
              <p className="text-sm text-gray-600">{t('topStories.noData')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">{t('topStories.table.story')}</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">{t('topStories.table.views')}</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">{t('topStories.table.reach')}</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">{t('topStories.table.engagement')}</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">{t('topStories.table.lastMeasured')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {topStories.map((story) => (
                      <tr key={story.story_id}>
                        <td className="px-4 py-3">
                          <div className="flex items-start space-x-3">
                            {(story.rendered_sas_url || story.sas_url) ? (
                              <img
                                src={story.rendered_sas_url || story.sas_url}
                                alt="Story thumbnail"
                                className="w-16 h-16 object-cover rounded flex-shrink-0"
                                onError={(e) => {
                                  e.target.style.display = 'none'
                                }}
                              />
                            ) : (
                              <div className="w-16 h-16 bg-gray-200 rounded flex-shrink-0 flex items-center justify-center">
                                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              {story.latest?.caption_text && (
                                <p className="text-xs text-gray-700 line-clamp-2">
                                  {story.latest.caption_text}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {numberFormatter.format(story.totals?.views || 0)}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {numberFormatter.format(story.totals?.reach || 0)}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {formatRate(story.average_rates?.engagement_rate)}
                        </td>
                        <td className="px-4 py-3 text-gray-500">
                          {story.latest?.measured_at
                            ? format(new Date(story.latest.measured_at), 'PPpp')
                            : '–'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  )
}

export default AnalyticsPage
